import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiFetch, BASE_URL } from "../../api/client";
import { COLORS, RADIUS } from "../../theme";
import Card from "../../components/ui/Card";
import Btn from "../../components/ui/Btn";
import Modal from "../../components/ui/Modal";
import Aviso from "../../components/ui/Aviso";
import EstadoCarregando from "../../components/ui/EstadoCarregando";
import EstadoVazio from "../../components/ui/EstadoVazio";
import { ThFiltroTexto, ThSimples, BarraResumoFiltro } from "../../components/ui/ThFiltro";

const TABELAS_RESUMO = [
  { chave: "usuarios", label: "usuários" },
  { chave: "funcionarios", label: "funcionários" },
  { chave: "postos", label: "postos" },
  { chave: "ponto_registros", label: "batidas de ponto" },
  { chave: "afastamentos", label: "afastamentos" },
];

function baixarJson(objeto, nomeArquivo) {
  const blob = new Blob([JSON.stringify(objeto, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminBackup() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [baixando, setBaixando] = useState(null);

  const [modalTenant, setModalTenant] = useState(null);
  const [arquivo, setArquivo] = useState(null); // { nomeArquivo, backup }
  const [erroArquivo, setErroArquivo] = useState("");
  const [nomeConfirmacao, setNomeConfirmacao] = useState("");
  const [restaurando, setRestaurando] = useState(false);
  const [erroRestaurar, setErroRestaurar] = useState("");
  const [resultado, setResultado] = useState(null); // { snapshot_anterior }
  const fileInputRef = useRef(null);
  const [filtroNome, setFiltroNome] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/admin/tenants", undefined, token);
      setTenants(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const baixarBackup = async (tenant) => {
    setBaixando(tenant.id);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/tenants/${tenant.id}/backup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Não foi possível gerar o backup.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-${tenant.nome.replace(/[^a-zA-Z0-9]+/g, "_")}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(e.message);
    } finally {
      setBaixando(null);
    }
  };

  const abrirRestaurar = (tenant) => {
    setModalTenant(tenant);
    setArquivo(null);
    setErroArquivo("");
    setNomeConfirmacao("");
    setErroRestaurar("");
    setResultado(null);
  };

  const selecionarArquivo = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setArquivo(null);
    setErroArquivo("");
    const leitor = new FileReader();
    leitor.onload = () => {
      try {
        const backup = JSON.parse(leitor.result);
        if (!backup?.tenant?.id) throw new Error("Arquivo não parece um backup do PontoZipar.");
        if (Number(backup.tenant.id) !== Number(modalTenant.id)) {
          setErroArquivo(`Esse arquivo é do backup de "${backup.tenant.nome}" — não pode ser restaurado em "${modalTenant.nome}".`);
          return;
        }
        setArquivo({ nomeArquivo: file.name, backup });
      } catch (err) {
        setErroArquivo("Não foi possível ler esse arquivo como um backup válido.");
      }
    };
    leitor.readAsText(file);
  };

  const restaurar = async () => {
    if (!arquivo || nomeConfirmacao.trim() !== modalTenant.nome.trim()) return;
    setRestaurando(true);
    setErroRestaurar("");
    try {
      const data = await apiFetch(
        "POST",
        `/api/admin/tenants/${modalTenant.id}/restaurar`,
        { backup: arquivo.backup, confirmar_nome: nomeConfirmacao.trim() },
        token
      );
      setResultado(data);
      carregar();
    } catch (e) {
      setErroRestaurar(e.message);
    } finally {
      setRestaurando(false);
    }
  };

  const tenantsFiltrados = tenants.filter((t) => !filtroNome || t.nome.toLowerCase().includes(filtroNome.toLowerCase()));

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
        Backup
      </h1>
      <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: "0 0 20px" }}>
        Baixe uma cópia completa dos dados de um cliente, ou restaure um cliente a partir de um backup baixado antes. Guarde os arquivos em local seguro fora do servidor.
      </p>

      {tenants.length > 0 && (
        <BarraResumoFiltro
          total={tenantsFiltrados.length}
          totalGeral={tenants.length}
          unidade="cliente(s)"
          filtrosAtivos={Boolean(filtroNome)}
          onLimpar={() => setFiltroNome("")}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : tenants.length === 0 ? (
          <EstadoVazio icon="briefcase" texto="Nenhum cliente cadastrado ainda." />
        ) : tenantsFiltrados.length === 0 ? (
          <EstadoVazio icon="briefcase" texto="Nenhum cliente encontrado com esse filtro." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <ThFiltroTexto label="Cliente" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar..." minWidth={170} />
                <ThSimples label="Usuários" />
                <ThSimples label="Funcionários" />
                <ThSimples label="Empresas" />
                <ThSimples label="" />
              </tr>
            </thead>
            <tbody>
              {tenantsFiltrados.map((t) => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{t.nome}</td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{t.total_usuarios}</td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{t.total_funcionarios}</td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{t.total_empresas}</td>
                  <td style={{ padding: "14px 20px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <Btn
                      variant="secondary"
                      onClick={() => baixarBackup(t)}
                      disabled={baixando === t.id}
                      style={{ padding: "6px 12px", fontSize: 12.5, marginRight: 8 }}
                    >
                      {baixando === t.id ? "Gerando..." : "Baixar backup"}
                    </Btn>
                    <Btn
                      variant="secondary"
                      onClick={() => abrirRestaurar(t)}
                      style={{ padding: "6px 12px", fontSize: 12.5, borderColor: COLORS.danger, color: COLORS.danger }}
                    >
                      Restaurar
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <p style={{ fontSize: 12, color: COLORS.textLight, margin: "16px 0 0" }}>
        O arquivo inclui todos os dados de cadastro e ponto do cliente, mas não inclui as fotos de selfie nem anexos de atestado (ficam salvos separadamente no servidor, não voltam em uma restauração).
      </p>

      <Modal
        open={!!modalTenant}
        onClose={() => setModalTenant(null)}
        title={modalTenant ? `Restaurar "${modalTenant.nome}"` : "Restaurar"}
        width={480}
      >
        {modalTenant && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {resultado ? (
              <>
                <Aviso tone="success" icon="checkCircle">
                  Restauração concluída. Os dados atuais de "{modalTenant.nome}" foram substituídos pelo backup enviado.
                </Aviso>
                <p style={{ fontSize: 12.5, color: COLORS.textMid, margin: 0 }}>
                  Antes de sobrescrever, o sistema guardou uma foto de como o cliente estava — baixe esse snapshot agora, caso precise desfazer.
                </p>
                <Btn
                  variant="secondary"
                  onClick={() => baixarJson(resultado.snapshot_anterior, `snapshot-antes-da-restauracao-${modalTenant.nome.replace(/[^a-zA-Z0-9]+/g, "_")}-${new Date().toISOString().slice(0, 10)}.json`)}
                >
                  Baixar snapshot de antes da restauração
                </Btn>
                <Btn onClick={() => setModalTenant(null)}>Fechar</Btn>
              </>
            ) : (
              <>
                <Aviso tone="danger" icon="alertCircle">
                  Isso substitui TODOS os dados atuais desse cliente pelos do arquivo — irreversível, exceto baixando o snapshot automático que o sistema tira antes de sobrescrever.
                </Aviso>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 }}>
                    Arquivo de backup
                  </label>
                  <input ref={fileInputRef} type="file" accept=".json" onChange={selecionarArquivo} style={{ display: "none" }} />
                  <Btn variant="secondary" onClick={() => fileInputRef.current?.click()} style={{ width: "100%" }}>
                    {arquivo ? `Selecionado: ${arquivo.nomeArquivo}` : "Escolher arquivo .json"}
                  </Btn>
                </div>

                {erroArquivo && (
                  <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
                    {erroArquivo}
                  </div>
                )}

                {arquivo && !erroArquivo && (
                  <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "12px 14px" }}>
                    <p style={{ fontSize: 12, color: COLORS.textMid, margin: "0 0 8px" }}>
                      Backup gerado em {new Date(arquivo.backup.gerado_em).toLocaleString("pt-BR")}, contém:
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {TABELAS_RESUMO.map((t) => (
                        <span key={t.chave} style={{ fontSize: 11.5, fontWeight: 600, color: COLORS.text, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 999, padding: "3px 10px" }}>
                          {(arquivo.backup[t.chave] || []).length} {t.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {arquivo && !erroArquivo && (
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 }}>
                      Digite "{modalTenant.nome}" pra confirmar
                    </label>
                    <input
                      value={nomeConfirmacao}
                      onChange={(e) => setNomeConfirmacao(e.target.value)}
                      placeholder={modalTenant.nome}
                      style={{ width: "100%", boxSizing: "border-box", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, color: COLORS.text }}
                    />
                  </div>
                )}

                {erroRestaurar && (
                  <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
                    {erroRestaurar}
                  </div>
                )}

                <Btn
                  variant="danger"
                  onClick={restaurar}
                  disabled={!arquivo || !!erroArquivo || nomeConfirmacao.trim() !== modalTenant.nome.trim() || restaurando}
                >
                  {restaurando ? "Restaurando..." : "Restaurar (substitui os dados atuais)"}
                </Btn>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
